import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, Eye, ShoppingCart, Maximize2, Edit, Trash2, MoreHorizontal, Flag, AlertTriangle } from 'lucide-react';
import { Post, ArtCategory } from '../types';
import { postsService } from '../services/postsService';
import EditPostModal from './EditPostModal';
import { useAuth } from '../context/AuthContext';
import FollowButton from './FollowButton';
import ShareModal from './ShareModal';
import PurchaseModal from './PurchaseModal';
import { adsDisplayService } from '../services/adsDisplayService';
import AdCard from './AdCard';
import ReportDialog from './ReportDialog';
import { reportsService } from '../services/reportsService';
import { useLanguage } from '../context/LanguageContext';
import { ContentModerationError } from '../lib/contentModeration';
import { useNavigate } from 'react-router-dom';

interface PostCardProps {
  post: Post;
  onLike: (postId: string) => void;
  onSave: (postId: string) => void;
}

const PostCard: React.FC<PostCardProps> = ({ post, onLike, onSave }) => {
  const { user } = useAuth();
  const { select, locale, t } = useLanguage();
  const navigate = useNavigate();
  const categoryLabels: Record<ArtCategory, string> = {
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Escultura' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Danza' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Maquillaje' }),
  };
  const adBadgeLabel = select({ es: 'Publicidad', en: 'Advertisement' });
  const sponsoredBadgeLabel = select({ es: 'Promocionado', en: 'Sponsored' });
  const rejectedOverlayLabel = select({ es: 'Publicación rechazada', en: 'Post rejected' });
  const commentPlaceholder = select({ es: 'Añade un comentario...', en: 'Add a comment...' });
  const sendLabel = select({ es: 'Enviar', en: 'Send' });
  const processingLabel = '...';

  const ctaButtonLabels: Record<string, { es: string; en: string }> = {
    learn_more: { es: '👀 Más info', en: '👀 Learn more' },
    shop_now: { es: '🛒 Comprar', en: '🛒 Shop now' },
    sign_up: { es: '✉️ Registrarse', en: '✉️ Sign up' },
    download: { es: '📥 Descargar', en: '📥 Download' },
    contact_us: { es: '📞 Contactar', en: '📞 Contact us' },
    book_now: { es: '📅 Reservar', en: '📅 Book now' },
  };

  const ctaChipLabels: Record<string, { es: string; en: string }> = {
    learn_more: { es: 'Más info', en: 'Learn more' },
    shop_now: { es: 'Comprar', en: 'Shop now' },
    sign_up: { es: 'Registrarse', en: 'Sign up' },
    download: { es: 'Descargar', en: 'Download' },
    contact_us: { es: 'Contactar', en: 'Contact us' },
    book_now: { es: 'Reservar', en: 'Book now' },
  };

  const getCtaButtonLabel = (cta: string) => select(ctaButtonLabels[cta] || { es: '👀 Ver más', en: '👀 View more' });
  const getCtaChipLabel = (cta: string) => select(ctaChipLabels[cta] || { es: 'Ver más', en: 'View more' });
  const formatCommentDate = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // Hooks declared BEFORE early return (Rules of Hooks)
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFeedback, setReportFeedback] = useState('');
  const [commentError, setCommentError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  const isAuthor = user?.id === post.author?.id;
  const isModerator = user?.user_type === 'moderator' || user?.user_type === 'admin';
  const moderationStatus = post.moderation_status || 'approved';
  const isPending = moderationStatus === 'pending';
  const isRejected = moderationStatus === 'rejected';
  const showModerationBanner = (isPending || isRejected) && (isAuthor || isModerator);

  useEffect(() => {
    // Reset state when post changes
    setLikesCount(post.likes || post.likes_count || 0);
    setIsLiked(false);
    setIsSaved(false);

    if (post.type === 'ad') return;

    checkIfLiked();
    checkIfSaved();
    incrementViews();
  }, [post.id, post.type]);

  // If this is an ad, use the simplified AdCard component
  if (post.type === 'ad') {
    return (
      <div className="flex items-center justify-center" style={{ alignSelf: 'center' }}>
        <AdCard post={post} />
      </div>
    );
  }

  const checkIfLiked = async () => {
    try {
      const liked = await postsService.checkIfLiked(post.id);
      setIsLiked(liked);
    } catch (err) {
      console.error('Error checking like status:', err);
    }
  };

  const checkIfSaved = async () => {
    try {
      const saved = await postsService.checkIfSaved(post.id);
      setIsSaved(saved);
    } catch (err) {
      console.error('Error checking save status:', err);
    }
  };

  const incrementViews = async () => {
    try {
      // Only increment views once per session for each post
      const viewedKey = `post_viewed_${post.id}`;
      if (sessionStorage.getItem(viewedKey)) {
        return; // Already viewed in this session
      }

      await postsService.incrementViews(post.id);
      sessionStorage.setItem(viewedKey, 'true');
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const navigateToProfile = () => {
    if (post.author?.username) {
      navigate(`/profile/${post.author.username}`);
    }
  };

  const handleAdClick = async () => {
    if (post.type === 'ad' && post.ad_id) {
      console.log('🖱️ Ad clicked in PostCard:', post.ad_id, post.title);
      console.log('🖱️ Ad clicked:', post.ad_id, post.title);
      // Track ad click
      await adsDisplayService.trackAdClick(post.ad_id, user?.id);
      console.log('✅ Ad click tracked in PostCard');

      // Open destination URL if available
      if (post.destination_url) {
        console.log('🌐 Opening destination URL from PostCard:', post.destination_url);
        window.open(post.destination_url, '_blank');
      } else {
        console.log('ℹ️ No destination URL for ad in PostCard');
      }
    }
  };

  const handleLike = () => {
    // Don't allow liking ads
    if (post.type === 'ad') return;

    if (isLiked) {
      // Unlike
      setIsLiked(false);
      setLikesCount(prev => Math.max(0, prev - 1));
    } else {
      // Like
      setIsLiked(true);
      setLikesCount(prev => prev + 1);
    }

    // Call parent handler
    onLike(post.id);
  };

  const handleSave = () => {
    // Don't allow saving ads
    if (post.type === 'ad') return;

    if (isSaved) {
      // Remove from wishlist
      postsService.removeFromWishlist(post.id).then(() => {
        setIsSaved(false);
      }).catch(err => {
        console.error('Error removing from wishlist:', err);
      });
    } else {
      // Add to wishlist
      postsService.addToWishlist(post.id).then(() => {
        setIsSaved(true);
      }).catch(err => {
        console.error('Error adding to wishlist:', err);
      });
    }
    onSave(post.id);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    // Don't allow commenting on ads
    if (post.type === 'ad') return;

    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const comment = await postsService.addComment(post.id, newComment.trim());

      // Update local comments state immediately
      post.comments = post.comments || [];
      post.comments.push(comment);

      // Update comments count
      if (post.comments_count !== undefined) {
        post.comments_count += 1;
      }

      // Clear input
      setNewComment('');
      setCommentError('');

      // Force re-render by updating a state that triggers re-render
      setSubmittingComment(false);
      setSubmittingComment(false); // This will trigger the parent to re-render
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setCommentError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error adding comment:', err);
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEdit = () => {
    setShowOptions(false);
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!window.confirm(select({
      es: '¿Estás seguro de que quieres eliminar esta publicación?',
      en: 'Are you sure you want to delete this post?',
    }))) {
      return;
    }

    setDeleting(true);
    try {
      await postsService.deletePost(post.id);
      // Refresh the page or remove the post from the list
      window.location.reload();
    } catch (err) {
      console.error('Error deleting post:', err);
      alert(select({ es: 'Error al eliminar la publicación', en: 'Failed to delete the post' }));
    } finally {
      setDeleting(false);
      setShowOptions(false);
    }
  };

  const openDetailModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // For ads, track click and open destination instead of detail modal
    if (post.type === 'ad') {
      handleAdClick();
      return;
    }

    navigate(`/post/${post.id}`);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);

  const handleReportSubmit = async ({ reason, description }: { reason: string; description?: string }) => {
    await reportsService.createReport({
      itemType: 'post',
      itemId: post.id,
      reason,
      description,
    });
    setReportFeedback(select({
      es: 'Reporte enviado. Gracias por ayudarnos a mantener la comunidad segura.',
      en: 'Report submitted. Thanks for helping us keep the community safe.',
    }));
    setTimeout(() => setReportFeedback(''), 6000);
  };

  return (
    <div className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 group/author">
          <div
            className="cursor-pointer relative"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToProfile();
            }}
          >
            <div className="absolute -inset-0.5 bg-gradient-to-tr from-purple-500 to-pink-500 rounded-full opacity-0 group-hover/author:opacity-100 transition-opacity duration-300"></div>
            <img
              src={post.author?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=50&h=50&fit=crop'}
              alt={post.author?.full_name || select({ es: 'Usuario', en: 'User' })}
              className="w-10 h-10 rounded-full object-cover"
            />
          </div>
          <div
            className="flex-1 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToProfile();
            }}
          >
            <h3 className="font-semibold text-gray-900">
              {post.author?.full_name || select({ es: 'Usuario desconocido', en: 'Unknown user' })}
            </h3>
            <p className="text-sm text-gray-500">
              @{post.author?.username || select({ es: 'usuario', en: 'user' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Follow Button */}
          <FollowButton
            userId={post.author?.id || ''}
            username={post.author?.username || ''}
            size="sm"
            variant="outline"
            showIcon={false}
          />

          {/* Options menu for own posts */}
          {user?.id === post.author?.id && (
            <div className="relative">
              {/* Visible Edit button for author */}
              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors mr-1"
                title={select({ es: 'Editar publicación', en: 'Edit post' })}
              >
                <Edit size={14} />
                {select({ es: 'Editar', en: 'Edit' })}
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <MoreHorizontal size={16} className="text-gray-500" />
              </button>

              {showOptions && (
                <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-32">
                  <button
                    onClick={handleEdit}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit size={14} />
                    {select({ es: 'Editar', en: 'Edit' })}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    {deleting
                      ? select({ es: 'Eliminando...', en: 'Deleting...' })
                      : select({ es: 'Eliminar', en: 'Delete' })}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showModerationBanner && (
        <div
          className={`mx-4 mb-3 px-4 py-3 rounded-xl border text-sm ${isPending
            ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className={isPending ? 'text-yellow-500 mt-0.5' : 'text-red-500 mt-0.5'} />
            <div>
              <p className="font-medium">
                {isPending
                  ? select({
                    es: 'Esta publicación está en revisión por el equipo de moderación.',
                    en: 'This post is under review by the moderation team.',
                  })
                  : select({
                    es: 'Esta publicación fue rechazada por el equipo de moderación.',
                    en: 'This post was rejected by the moderation team.',
                  })}
              </p>
              {post.moderation_reason && (
                <p className="mt-1 text-sm opacity-80">
                  {select({ es: 'Motivo:', en: 'Reason:' })} {post.moderation_reason}
                </p>
              )}
              {post.moderated_at && (
                <p className="mt-1 text-xs opacity-60">
                  {select({ es: 'Última revisión:', en: 'Last review:' })} {new Date(post.moderated_at).toLocaleString(locale)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Media */}
      <div
        className="relative group select-none"
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        {showModerationBanner && (
          <div
            className={`absolute top-4 left-4 z-20 px-3 py-1 rounded-full text-xs font-semibold shadow ${isPending ? 'bg-yellow-500 text-white' : 'bg-red-600 text-white'
              }`}
          >
            {isPending
              ? select({ es: 'En revisión', en: 'Under review' })
              : select({ es: 'Rechazada', en: 'Rejected' })}
          </div>
        )}

        {isRejected && (isAuthor || isModerator) && (
          <div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2 px-6 text-center">
            <AlertTriangle size={36} className="text-red-200" />
            <p className="text-white font-semibold">{rejectedOverlayLabel}</p>
            {post.moderation_reason && (
              <p className="text-sm text-red-100">
                {select({ es: 'Motivo:', en: 'Reason:' })} {post.moderation_reason}
              </p>
            )}
          </div>
        )}
        {post.media && post.media[0]?.type === 'image' && (
          <div className="relative">
            <img
              src={post.media[0].url}
              alt={post.title}
              className="w-full h-80 object-cover protected-media"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
            />
            <div className="watermark-overlay"></div>
            <div className="security-overlay"></div>
          </div>
        )}
        {post.media && post.media[0]?.type === 'video' && (
          <div className="relative">
            <video
              src={post.media[0].url}
              muted
              className="w-full h-80 object-cover protected-media"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              draggable={false}
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
            />
            <div className="watermark-overlay"></div>
            <div className="security-overlay"></div>
          </div>
        )}

        {/* Audio/Music - Show cover image or default */}
        {post.media && post.media[0]?.type === 'audio' && (
          <div className="w-full h-80 relative select-none" onContextMenu={(e) => e.preventDefault()}>
            {/* Look for cover image (should be the first image in media array, or use default) */}
            {post.media.find(m => m.type === 'image') ? (
              <div className="relative w-full h-full">
                <img
                  src={post.media.find(m => m.type === 'image')!.url}
                  alt={post.title}
                  className="w-full h-full object-cover protected-media"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  draggable={false}
                />
                <div className="watermark-overlay"></div>
                <div className="security-overlay"></div>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-6xl mb-4">🎵</div>
                  <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                  <p className="text-white text-opacity-80">
                    {select({ es: 'Por', en: 'By' })} {post.author.full_name}
                  </p>
                </div>
              </div>
            )}
            {/* Audio indicator */}
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
              <span>🎵</span>
              {select({ es: 'Audio', en: 'Audio' })}
            </div>
          </div>
        )}

        {/* Document/PDF/Ebook - Show cover image or default */}
        {post.media && post.media[0]?.type === 'document' && (
          <div className="w-full h-80 relative select-none" onContextMenu={(e) => e.preventDefault()}>
            {/* Look for cover image (should be the first image in media array, or use default) */}
            {post.media.find(m => m.type === 'image') ? (
              <div className="relative w-full h-full">
                <img
                  src={post.media.find(m => m.type === 'image')!.url}
                  alt={post.title}
                  className="w-full h-full object-cover protected-media"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  draggable={false}
                />
                <div className="watermark-overlay"></div>
                <div className="security-overlay"></div>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                  <p className="text-white text-opacity-80">
                    {select({ es: 'Por', en: 'By' })} {post.author.full_name}
                  </p>
                </div>
              </div>
            )}
            {/* Document indicator */}
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1 z-20">
              <span>📄</span>
              {post.media[0]?.file_name?.includes('.pdf') ? 'PDF' :
                post.media[0]?.file_name?.includes('.epub') ? 'EPUB' : select({ es: 'Documento', en: 'Document' })}
            </div>
          </div>
        )}

        {/* Default placeholder if no media */}
        {(!post.media || post.media.length === 0) && (
          <div className="w-full h-80 bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center select-none" onContextMenu={(e) => e.preventDefault()}>
            <div className="text-white text-center">
              <div className="text-4xl mb-2">🎨</div>
              <p className="font-medium">{select({ es: 'Arte conceptual', en: 'Conceptual art' })}</p>
            </div>
          </div>
        )}

        {post.for_sale && (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-sm font-medium flex items-center gap-1 z-20">
            <ShoppingCart size={14} />
            {formatPrice(post.price!)}
          </div>
        )}
      </div>

      {/* View Detail Button */}
      {post.type !== 'ad' && (
        <div className="px-4 pt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDetailModal(e);
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md"
          >
            <Maximize2 size={16} />
            <span className="font-medium">{select({ es: 'Ver en detalle', en: 'View details' })}</span>
          </button>

          {/* Purchase Button for Sale Posts */}
          {post.for_sale && post.price && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPurchaseModal(true);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md mt-2"
            >
              <ShoppingCart size={16} />
              <span className="font-medium">
                {select({ es: 'Comprar', en: 'Buy' })} {formatPrice(post.price)}
              </span>
            </button>
          )}
        </div>
      )}

      {/* CTA Button for Ads */}
      {post.type === 'ad' && post.call_to_action && (
        <div className="px-4 pt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAdClick();
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md font-bold"
          >
            {getCtaButtonLabel(post.call_to_action)}
          </button>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Sponsored Badge */}
        {(post.is_sponsored || post.type === 'ad') && (
          <div className="mb-3 flex items-center justify-between">
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-medium rounded-full flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              {post.type === 'ad' ? adBadgeLabel : sponsoredBadgeLabel}
            </span>
            {post.type === 'ad' && post.call_to_action && (
              <button
                onClick={handleAdClick}
                className="px-3 py-1 bg-white text-blue-600 text-xs font-bold rounded-full border border-blue-200 hover:bg-blue-50 transition-all transform hover:scale-105"
              >
                {getCtaChipLabel(post.call_to_action)}
              </button>
            )}
          </div>
        )}

        <h2 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h2>
        <p className="text-gray-700 mb-3 line-clamp-3">{post.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags && post.tags.map((tag, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                // Dispatch custom event to navigate to explore with tag
                const event = new CustomEvent('navigate', {
                  detail: {
                    tab: 'explore',
                    searchQuery: tag
                  }
                });
                window.dispatchEvent(event);
              }}
              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-purple-100 hover:text-purple-700 transition-all duration-200 cursor-pointer transform hover:scale-105"
            >
              #{tag}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {post.type !== 'ad' ? (
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                  }`}
              >
                <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
                <span className="text-sm font-medium">{likesCount}</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 text-gray-400">
                <Heart size={20} />
                <span className="text-sm">—</span>
              </div>
            )}

            {post.type !== 'ad' ? (
              <button
                onClick={() => setShowComments(!showComments)}
                className="flex items-center gap-2 text-gray-500 hover:text-blue-500 transition-colors"
              >
                <MessageCircle size={20} />
                <span className="text-sm font-medium">{post.comments?.length || post.comments_count || 0}</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 text-gray-400">
                <MessageCircle size={20} />
                <span className="text-sm">—</span>
              </div>
            )}

            {post.type !== 'ad' && (
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 text-gray-500 hover:text-green-500 transition-colors"
              >
                <Share2 size={20} />
              </button>
            )}

            {!isAuthor && post.type !== 'ad' && (
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
                title={select({ es: 'Reportar publicación', en: 'Report post' })}
              >
                <Flag size={20} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-gray-500">
              <Eye size={16} />
              <span className="text-sm">{post.views || post.views_count}</span>
            </div>

            {post.type !== 'ad' ? (
              <button
                onClick={handleSave}
                className={`transition-colors ${isSaved ? 'text-purple-500' : 'text-gray-500 hover:text-purple-500'
                  }`}
              >
                <Bookmark size={20} fill={isSaved ? 'currentColor' : 'none'} />
              </button>
            ) : (
              <button
                onClick={handleAdClick}
                className="text-blue-600 hover:text-blue-700 transition-colors"
                title={select({ es: 'Ver anuncio', en: 'View ad' })}
              >
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold">AD</span>
                </div>
              </button>
            )}
          </div>
        </div>

        {reportFeedback && !isAuthor && (
          <div className="mt-3 text-sm text-green-600">{reportFeedback}</div>
        )}

        {/* Footer with category and premium */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            {categoryLabels[post.category as ArtCategory] || post.category}
          </span>
          {(post.author?.is_premium || post.is_premium) && (
            <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-medium rounded-full">
              {select({ es: '✨ Premium', en: '✨ Premium' })}
            </span>
          )}
        </div>
      </div>

      {/* Comments Section */}
      {showComments && post.type !== 'ad' && (
        <div className="border-t border-gray-100 p-4 max-h-96 overflow-y-auto">
          {commentError && (
            <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
              {commentError}
            </div>
          )}
          <form onSubmit={handleSubmitComment} className="flex gap-3 mb-4">
            <img
              src="https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=40&h=40&fit=crop"
              alt={select({ es: 'Tu avatar', en: 'Your avatar' })}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => {
                    setCommentError('');
                    setNewComment(e.target.value);
                  }}
                  placeholder={commentPlaceholder}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={submittingComment || !newComment.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {submittingComment ? processingLabel : sendLabel}
                </button>
              </div>
            </div>
          </form>

          {/* Existing Comments */}
          {post.comments && post.comments.length > 0 && (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {post.comments.map((comment: Record<string, unknown>) => (
                <div key={comment.id} className="flex gap-3">
                  <img
                    src={comment.profiles?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=32&h=32&fit=crop'}
                    alt={comment.profiles?.full_name || select({ es: 'Usuario', en: 'User' })}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="bg-gray-100 rounded-lg px-3 py-2">
                      <p className="font-medium text-sm text-gray-900">
                        {comment.profiles?.full_name || select({ es: 'Usuario desconocido', en: 'Unknown user' })}
                      </p>
                      <p className="text-gray-700 break-words">{comment.content}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <button
                        onClick={async () => {
                          try {
                            // Add like functionality for comments
                            await postsService.likeComment(comment.id);
                            // Refresh comments or update local state
                          } catch (err) {
                            console.error('Error liking comment:', err);
                          }
                        }}
                        className="hover:text-red-500 flex items-center gap-1"
                      >
                        <Heart size={12} />
                        <span>{comment.likes_count || 0}</span>
                      </button>
                      <span>•</span>
                      <span>{formatCommentDate(comment.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detail Modal is now handled by routing via PostDetailView */}

      {/* Share Modal */}
      {showShareModal && post.type !== 'ad' && typeof document !== 'undefined' && (
        <>
          {/* Create portal effect by rendering outside component */}
          <ShareModal
            post={post}
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
          />
        </>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && post.type !== 'ad' && (
        <PurchaseModal
          post={post}
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={() => {
            console.log('Purchase completed successfully!');
            setShowPurchaseModal(false);
          }}
        />
      )}

      {/* Edit Post Modal */}
      {showEditModal && post.type !== 'ad' && (
        <EditPostModal
          isOpen={showEditModal}
          post={post}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            window.location.reload();
          }}
        />
      )}

      {post.type !== 'ad' && (
        <ReportDialog
          isOpen={showReportModal}
          title={select({ es: 'Reportar publicación', en: 'Report post' })}
          itemDescription={post.title}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportSubmit}
        />
      )}
    </div>
  );
};

export default PostCard;
